import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { fetchMe, logoutCustomer, isSignedIn } from "@/lib/customerAuth";

/**
 * /account (Design Specification Section 12.4). "Your enrollments" needs a customer-scoped enrollments list
 * endpoint that does not exist yet (open item) — shown honestly as not-yet-available rather than an empty
 * list, which would read as "you have none" when the real answer is "we cannot show you yet".
 */
export default function AccountOverview() {
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSignedIn()) {
      navigate("/account/login", { state: { from: "/account" } });
      return;
    }
    let cancelled = false;
    fetchMe()
      .then((c) => { if (!cancelled) { setCustomer(c); setStatus("ok"); } })
      .catch((err) => {
        if (cancelled) return;
        if (err && err.response && err.response.status === 401) {
          navigate("/account/login", { state: { from: "/account" }, replace: true });
        } else {
          setStatus("error");
        }
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const signOut = async () => {
    await logoutCustomer();
    navigate("/account/login", { replace: true });
  };

  if (status === "loading") {
    return <div className="max-w-[900px] mx-auto px-6 py-16 text-slate-500" role="status" data-testid="account-page">Loading…</div>;
  }
  if (status === "error") {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-16" data-testid="account-page">
        <Seo title="Your account" noindex path="/account" />
        <p role="alert">We could not load your account right now. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 py-16" data-testid="account-page">
      <Seo title="Your account" noindex path="/account" />
      <h1 tabIndex={-1} className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-8">Your account</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="border border-slate-200 rounded-2xl p-6" data-testid="account-card-profile">
          <h2 className="font-display text-lg font-bold text-[#0A2540] mb-3">Your account</h2>
          <p className="text-slate-700">{customer.email}</p>
          <span
            className={`inline-flex items-center mt-3 px-3 py-1 rounded-full text-xs font-semibold ${
              customer.email_verified ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF2F2] text-[#B91C1C]"
            }`}
            data-testid="account-email-status"
          >
            {customer.email_verified ? "Email confirmed" : "Email not confirmed"}
          </span>
          {!customer.email_verified && (
            <p className="mt-2 text-sm">
              <Link to="/account/verify" className="underline">Confirm your email</Link>
            </p>
          )}
        </section>

        <section className="border border-slate-200 rounded-2xl p-6" data-testid="account-card-enrollments">
          <h2 className="font-display text-lg font-bold text-[#0A2540] mb-3">Your enrollments</h2>
          <p className="text-slate-600 text-sm" data-testid="account-enrollments-unavailable">
            We cannot show your enrollment history here yet. If you need the status of a request, use the reference
            number from your confirmation, or contact us.
          </p>
          <p className="mt-3 text-sm">
            <Link to="/texas/plans" className="underline">Browse Texas plans</Link>
          </p>
        </section>

        <section className="border border-slate-200 rounded-2xl p-6 md:col-span-2" data-testid="account-card-privacy">
          <h2 className="font-display text-lg font-bold text-[#0A2540] mb-3">Privacy requests</h2>
          <p className="text-slate-600 text-sm">
            To request a copy, correction, or deletion of your information, contact us. This page will carry a direct
            form once Witherspoon-CLO finalizes the request wording and destination.
          </p>
        </section>
      </div>

      <button type="button" onClick={signOut} className="btn-outline mt-8" data-testid="account-signout">
        Sign out
      </button>
    </div>
  );
}
